import React, { useCallback, useMemo, useRef } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { IoCheckmark } from 'react-icons/io5';
import { IoIosSend } from 'react-icons/io';
import { LuUpload } from 'react-icons/lu';
import styles from './phase-sidebar.module.scss';
import { IPhase } from '~/types/estimates';
import { isRentalRowPersistable } from '~/utils/etaiement-rentals';
import { useGetEtaiement } from '~/hooks/etaiements';
import Modal from '~/lib/Modal';
import ModalExport from '../ModalExport';

type LinkId = {
  value: string,
  label: string,
};

/** Classic étaiement phases only (no ascenseur / parapluie / grue). */
export const etaiementPhasesMenu : LinkId[] = [
  { label: 'Dimension', value: '#dimension' },
  { label: 'Manutention', value: '#manutention' },
  { label: 'Transport', value: '#transport' },
  { label: 'Prestations', value: '#prestations' },
  { label: 'Location', value: '#location' },
  { label: 'Montage/Démontage', value: '#montage' },
  { label: 'Frais d’études', value: '#study' },
  { label: 'Résumé', value: '#summary' },
];

const PhaseSidebar = ({ isPreview }: { isPreview: boolean }) => {
  const location = useLocation();
  const { id, phaseId, ...params } = useParams();

  const exportRef = useRef<any>();
  const sendRef = useRef<any>();

  const {
    data: etaiement,
  } = useGetEtaiement(id || '');

  const phase : any = useMemo(() => {
    const found = etaiement?.phases?.find((d: IPhase) => d._id === phaseId || d._id === params['*']);
    return found;
  }, [etaiement, phaseId, params]);

  const getPhaseValidation = useCallback((type: string) => {
    let isValid = false;
    if (type === '#dimension' && phase?.dimension?.tonnage) {
      isValid = true;
    }
    if (type === '#montage' && phase?.mobilization?.type) {
      isValid = true;
    }
    if (type === '#location' && (
      (Array.isArray(phase?.rentals) && phase.rentals.some(isRentalRowPersistable))
      || phase?.rental?.duration
    )) {
      isValid = true;
    }
    if (type === '#transport' && phase?.transport?.type && phase?.transport?.type !== 'Aucun') {
      isValid = true;
    }
    if (type === '#manutention' && phase?.handling?.assembly?.type && phase?.handling?.assembly?.type !== 'Aucun') {
      isValid = true;
    }
    if (type === '#prestations' && Array.isArray(phase?.prestations) && phase.prestations.length > 0) {
      isValid = true;
    }
    if (type === '#study' && phase?.study) {
      isValid = true;
    }
    return isValid;
  }, [phase]);

  return (
    <>
      <div className={styles.sidebar}>
        {etaiement?._id
          && <div className={styles.actions}>
            {!isPreview
              && <button
                onClick={() => sendRef.current.open()}
              >
                {IoIosSend({})}Envoyer
              </button>
            }
            <button
              onClick={() => exportRef.current.open()}
            >
              {LuUpload({})}Exporter
            </button>
          </div>
        }
        <hr />
        <ul>
          {etaiementPhasesMenu.map((link: LinkId) => <li
            key={link.value}
            className={location.hash === link.value ? styles.active : ''}
          >
            <Link to={`${link.value}`}>
              {getPhaseValidation(link.value) ? IoCheckmark({ color: '#6AA187', size: 18 }) : null}
              <p>{link.label}</p>
            </Link>
          </li>)}
        </ul>
      </div>
      {etaiement?._id
      && <>
        <Modal ref={exportRef} width={700}>
          <ModalExport
            etaiement={etaiement}
            close={() => exportRef?.current.close()}
          />
        </Modal>
        <Modal ref={sendRef} width={700}>
          <ModalExport
            title='Envoyer le devis'
            etaiement={etaiement}
            close={() => sendRef?.current.close()}
          />
        </Modal>
      </>
      }
    </>
  );
};

export default PhaseSidebar;
